/**
 * Command Center - Premium Operations Console v3.5
 * Futuristic Mission Control for Campus Events
 * 
 * Design Philosophy:
 * - Dark glass panels with premium depth
 * - Subtle glow accents that feel alive
 * - Elegant spacing and visual hierarchy
 * - Tesla/SpaceX mission control aesthetic
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useWebSocket, useWebSocketEvent, WS_EVENTS } from '@/context/WebSocketContext';
import { cn } from '@/lib/utils';
import { QuickStatsWidget } from '@/components/dashboard/QuickStatsWidget';
import {
  Plus, UserPlus, Calendar, TrendingUp, Users, Trophy, Clock, Zap,
  ArrowRight, Activity, Radio, Gauge, Scan, AlertTriangle,
  Wifi, WifiOff, Bell, DollarSign, Globe, Radar, Megaphone, Award,
  UserCheck, Star, Crown, Ticket, Building2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================================================
// PREMIUM DESIGN TOKENS
// ============================================================================

const COLORS = {
  obsidian: '#0A0A0B',
  graphite: '#16181D',
  panelBg: 'rgba(10, 10, 11, 0.85)',
  borderSubtle: 'rgba(255, 255, 255, 0.04)',
  borderLight: 'rgba(255, 255, 255, 0.08)',
  borderActive: 'rgba(255, 255, 255, 0.12)',
  textPrimary: '#F5F7FA',
  textSecondary: '#C9CED6',
  textMuted: 'rgba(201, 206, 214, 0.5)',
  electricBlue: '#4DA3FF',
  coolMint: '#5EEAD4',
  softAmber: '#F7C873',
  signalRed: '#FF5A5F',
  violet: '#A78BFA',
};

// ============================================================================
// DEMO DATA
// ============================================================================

const generateDemoEvents = () => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  return [
    { id: 'demo-1', event_name: 'Neon Night', organization_name: 'Sigma Chi', location: 'Main Ballroom', start_time: new Date(today.getTime() + 20 * 60 * 60 * 1000).toISOString(), capacity: 350, guest_count: 312, checked_in_count: 187 },
    { id: 'demo-2', event_name: 'Fall Formal', organization_name: 'Delta Gamma', location: 'Grand Hall', start_time: new Date(today.getTime() + 19 * 60 * 60 * 1000).toISOString(), capacity: 200, guest_count: 195, checked_in_count: 168 },
    { id: 'demo-3', event_name: 'Greek Week', organization_name: 'IFC Council', location: 'Student Center', start_time: new Date(today.getTime() + 21 * 60 * 60 * 1000).toISOString(), capacity: 500, guest_count: 420, checked_in_count: 89 },
    { id: 'demo-4', event_name: 'DJ Night', organization_name: 'Campus Events', location: 'Rooftop Lounge', start_time: new Date(today.getTime() + 22 * 60 * 60 * 1000).toISOString(), capacity: 150, guest_count: 128, checked_in_count: 45 },
    { id: 'demo-5', event_name: 'Alumni Mixer', organization_name: 'Beta Theta Pi', location: 'Chapter House', start_time: new Date(today.getTime() + 18 * 60 * 60 * 1000).toISOString(), capacity: 80, guest_count: 75, checked_in_count: 62 },
  ];
};

const generateDemoSponsors = () => [
  { id: 's1', name: "Tony's Pizza", status: 'active', impressions: 4812, clicks: 221, revenue: 312 },
  { id: 's2', name: 'Campus Gym', status: 'active', impressions: 3120, clicks: 94, revenue: 141 },
  { id: 's3', name: 'Coffee House', status: 'active', impressions: 5240, clicks: 312, revenue: 468 },
  { id: 's4', name: 'Greek Gear', status: 'paused', impressions: 1850, clicks: 67, revenue: 89 },
  { id: 's5', name: 'Local Brew', status: 'active', impressions: 2890, clicks: 156, revenue: 234 },
];

const generateDemoCampusStats = () => ({ eventsTonight: 5, activeCheckIns: 551, studentsOnline: 1247, totalAttendanceToday: 892, revenueToday: 2156 });

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const getEventStatus = (startTime) => {
  const now = new Date();
  const start = new Date(startTime);
  const end = new Date(start.getTime() + 4 * 60 * 60 * 1000);
  if (now < start) return 'upcoming';
  if (now >= start && now <= end) return 'live';
  return 'ended';
};

const isToday = (dateString) => new Date().toDateString() === new Date(dateString).toDateString();

// ============================================================================
// PREMIUM COMPONENTS
// ============================================================================

// Animated Number Display
const AnimatedValue = ({ value, prefix = '', suffix = '', className }) => {
  const [display, setDisplay] = useState(0);
  const prevRef = useRef(value);

  useEffect(() => {
    const start = prevRef.current;
    const end = typeof value === 'number' ? value : parseInt(value) || 0;
    const duration = 800;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setDisplay(Math.round(start + (end - start) * eased));
      if (progress < 1) requestAnimationFrame(animate);
      else prevRef.current = end;
    };
    requestAnimationFrame(animate);
  }, [value]);

  return <span className={className}>{prefix}{display.toLocaleString()}{suffix}</span>;
};

// Glass Panel - Premium container with depth
const GlassPanel = ({ children, className, glow, interactive, ...props }) => (
  <div
    className={cn(
      'relative rounded-2xl overflow-hidden',
      'bg-gradient-to-b from-white/[0.03] to-transparent',
      'border border-white/[0.04]',
      'backdrop-blur-xl',
      interactive && 'transition-all duration-500 hover:border-white/[0.08] hover:shadow-[0_8px_32px_rgba(0,0,0,0.4)]',
      glow && 'shadow-[0_0_60px_rgba(77,163,255,0.06)]',
      className
    )}
    style={{ backgroundColor: COLORS.panelBg }}
    {...props}
  >
    <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] via-transparent to-transparent pointer-events-none" />
    <div className="relative z-10 h-full">{children}</div>
  </div>
);

// Module Header
const ModuleHeader = ({ icon: Icon, title, subtitle, status, action }) => (
  <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.03]">
    <div className="flex items-center gap-3">
      {Icon && (
        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-white/[0.06] to-white/[0.02] flex items-center justify-center border border-white/[0.04]">
          <Icon className="w-4 h-4 text-soft-silver/80" />
        </div>
      )}
      <div>
        <h3 className="text-[11px] font-semibold text-cloud-white uppercase tracking-[0.12em]">{title}</h3>
        {subtitle && <p className="text-[9px] font-mono text-white/30 uppercase tracking-wider mt-0.5">{subtitle}</p>}
      </div>
    </div>
    <div className="flex items-center gap-3">
      {status && <StatusOrb status={status} />}
      {action}
    </div>
  </div>
);

// Status Orb - Elegant glowing indicator
const StatusOrb = ({ status = 'idle', label, size = 'sm' }) => {
  const configs = {
    live: { color: COLORS.coolMint, glow: 'rgba(94,234,212,0.6)', pulse: true },
    connected: { color: COLORS.electricBlue, glow: 'rgba(77,163,255,0.6)', pulse: true },
    warning: { color: COLORS.softAmber, glow: 'rgba(247,200,115,0.5)', pulse: false },
    error: { color: COLORS.signalRed, glow: 'rgba(255,90,95,0.5)', pulse: true },
    idle: { color: 'rgba(201,206,214,0.3)', glow: 'none', pulse: false },
  };
  const config = configs[status] || configs.idle;
  const sizes = { sm: 6, md: 8, lg: 10 };

  return (
    <div className="flex items-center gap-2">
      <div className="relative" style={{ width: sizes[size], height: sizes[size] }}>
        {config.pulse && (
          <span 
            className="absolute inset-0 rounded-full animate-ping opacity-50"
            style={{ backgroundColor: config.color }}
          />
        )}
        <span 
          className="absolute inset-0 rounded-full transition-all duration-300"
          style={{ backgroundColor: config.color, boxShadow: `0 0 ${sizes[size] * 1.5}px ${config.glow}` }}
        />
      </div>
      {label && (
        <span className="text-[9px] font-mono uppercase tracking-wider" style={{ color: config.color }}>
          {label}
        </span>
      )}
    </div>
  );
};

// Metric Cell - Clean telemetry display
const MetricCell = ({ label, value, prefix = '', suffix = '', trend, animated = true, size = 'md', icon: Icon }) => {
  const sizes = { sm: 'text-lg', md: 'text-2xl', lg: 'text-3xl', xl: 'text-4xl' };
  
  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-1.5 mb-1.5">
        {Icon && <Icon className="w-3 h-3 text-white/30" />}
        <span className="text-[9px] font-mono text-white/30 uppercase tracking-[0.1em]">{label}</span>
      </div>
      <div className="flex items-baseline gap-1.5">
        {animated ? (
          <AnimatedValue value={value} prefix={prefix} className={cn('font-mono font-semibold text-cloud-white tracking-tight', sizes[size])} />
        ) : (
          <span className={cn('font-mono font-semibold text-cloud-white tracking-tight', sizes[size])}>{prefix}{value}</span>
        )}
        {suffix && <span className="text-xs font-mono text-white/30">{suffix}</span>}
        {trend && (
          <span className={cn('text-[10px] font-mono ml-1', trend > 0 ? 'text-cool-mint' : 'text-signal-red')}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
    </div>
  );
};

// Progress Arc - Circular gauge
const ProgressArc = ({ value, max = 100, size = 90, strokeWidth = 5 }) => {
  const percentage = Math.min((value / max) * 100, 100);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;
  const color = percentage >= 90 ? COLORS.signalRed : percentage >= 70 ? COLORS.softAmber : COLORS.coolMint;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth={strokeWidth} />
        <circle 
          cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth}
          strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
          className="transition-all duration-1000 ease-out"
          style={{ filter: `drop-shadow(0 0 8px ${color}50)` }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <AnimatedValue value={Math.round(percentage)} suffix="%" className="text-xl font-mono font-semibold text-cloud-white" />
        <span className="text-[8px] font-mono text-white/30 uppercase tracking-wider">Capacity</span>
      </div>
    </div>
  );
};

// Command Button - Premium action button
const CommandButton = ({ icon: Icon, label, onClick, variant = 'default', glow }) => {
  const variants = {
    default: 'from-white/[0.03] to-white/[0.01] border-white/[0.06] hover:border-white/[0.12] text-soft-silver hover:text-cloud-white',
    primary: 'from-electric-ice-blue/15 to-electric-ice-blue/5 border-electric-ice-blue/20 hover:border-electric-ice-blue/40 text-electric-ice-blue',
    success: 'from-cool-mint/15 to-cool-mint/5 border-cool-mint/20 hover:border-cool-mint/40 text-cool-mint',
    warning: 'from-soft-amber/15 to-soft-amber/5 border-soft-amber/20 hover:border-soft-amber/40 text-soft-amber',
  };

  return (
    <button
      onClick={onClick}
      className={cn(
        'group relative flex flex-col items-center gap-2 px-5 py-3.5 rounded-xl',
        'bg-gradient-to-b border backdrop-blur-sm',
        'transition-all duration-300 ease-out',
        'hover:-translate-y-0.5 hover:shadow-lg',
        variants[variant],
        glow && 'shadow-[0_0_20px_rgba(77,163,255,0.1)]'
      )}
    >
      <Icon className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" />
      <span className="text-[9px] font-mono uppercase tracking-[0.1em]">{label}</span>
    </button>
  );
};

// ============================================================================
// EVENT RADAR
// ============================================================================

const EventRadar = ({ events, onEventClick, pulseEvent }) => {
  const canvasRef = useRef(null);
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const animationRef = useRef(null);
  const sweepAngleRef = useRef(0);
  const pulseRef = useRef({});

  const tonightsEvents = useMemo(() => events.filter(e => isToday(e.start_time)), [events]);
  
  const eventNodes = useMemo(() => {
    const count = tonightsEvents.length || 1;
    return tonightsEvents.map((event, index) => {
      const isLive = getEventStatus(event.start_time) === 'live';
      const checkedIn = event.checked_in_count || 0;
      const capacity = event.capacity || 100;
      const fillRate = checkedIn / capacity;
      
      // Distribute events evenly around the circle with offset to avoid top position
      const angleOffset = -Math.PI / 2 + Math.PI / 6; // Start from slightly right of top
      const angle = angleOffset + (index / count) * Math.PI * 2;
      
      // Use different radii based on index to spread events across rings
      const ringIndex = index % 3; // 3 different ring levels
      const baseRadius = 0.45 + ringIndex * 0.18; // 0.45, 0.63, 0.81
      
      return { 
        ...event, 
        angle, 
        radius: baseRadius, 
        isLive, 
        fillRate, 
        checkedIn, 
        capacity, 
        nodeSize: 10 + (fillRate * 8) // Smaller nodes for cleaner look
      };
    });
  }, [tonightsEvents]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    const maxR = Math.min(cx, cy) - 40; // More padding for labels

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Rings - cleaner with fewer rings
      for (let i = 1; i <= 3; i++) {
        const r = (maxR / 3) * i;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(77, 163, 255, ${0.06 + i * 0.02})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Crosshairs - subtle
      ctx.strokeStyle = 'rgba(77, 163, 255, 0.05)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(cx, cy - maxR); ctx.lineTo(cx, cy + maxR);
      ctx.moveTo(cx - maxR, cy); ctx.lineTo(cx + maxR, cy);
      ctx.stroke();

      // Sweep line
      sweepAngleRef.current += 0.01;
      const sweepX = cx + Math.cos(sweepAngleRef.current) * maxR;
      const sweepY = cy + Math.sin(sweepAngleRef.current) * maxR;
      
      const grad = ctx.createLinearGradient(cx, cy, sweepX, sweepY);
      grad.addColorStop(0, 'rgba(77, 163, 255, 0)');
      grad.addColorStop(1, 'rgba(77, 163, 255, 0.5)');
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(sweepX, sweepY);
      ctx.strokeStyle = grad;
      ctx.lineWidth = 2;
      ctx.stroke();

      // Sweep trail
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, maxR, sweepAngleRef.current - 0.5, sweepAngleRef.current);
      ctx.closePath();
      const trailGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, maxR);
      trailGrad.addColorStop(0, 'rgba(77, 163, 255, 0)');
      trailGrad.addColorStop(0.5, 'rgba(77, 163, 255, 0.03)');
      trailGrad.addColorStop(1, 'rgba(77, 163, 255, 0.08)');
      ctx.fillStyle = trailGrad;
      ctx.fill();

      // Draw event nodes
      eventNodes.forEach((event, idx) => {
        const x = cx + Math.cos(event.angle) * (event.radius * maxR);
        const y = cy + Math.sin(event.angle) * (event.radius * maxR);
        
        if (!pulseRef.current[event.id]) pulseRef.current[event.id] = { phase: Math.random() * Math.PI * 2 };
        pulseRef.current[event.id].phase += 0.04;
        const pulse = event.isLive ? Math.sin(pulseRef.current[event.id].phase) * 0.15 + 1 : 1;
        
        const color = event.isLive ? COLORS.coolMint : COLORS.electricBlue;
        const size = event.nodeSize * pulse;

        // Outer glow
        const glow = ctx.createRadialGradient(x, y, 0, x, y, size * 2.5);
        glow.addColorStop(0, event.isLive ? 'rgba(94,234,212,0.35)' : 'rgba(77,163,255,0.25)');
        glow.addColorStop(1, 'transparent');
        ctx.beginPath();
        ctx.arc(x, y, size * 2.5, 0, Math.PI * 2);
        ctx.fillStyle = glow;
        ctx.fill();

        // Main node
        ctx.beginPath();
        ctx.arc(x, y, size, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();

        // Inner core
        ctx.beginPath();
        ctx.arc(x, y, size * 0.4, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.fill();

        // Label - positioned smartly based on angle
        const labelOffset = size + 14;
        let labelX = x;
        let labelY = y + labelOffset;
        
        // Adjust label position based on quadrant to avoid overlap
        const angleDeg = ((event.angle * 180 / Math.PI) + 360) % 360;
        if (angleDeg > 45 && angleDeg < 135) {
          // Bottom - label below
          labelY = y + labelOffset;
        } else if (angleDeg >= 135 && angleDeg < 225) {
          // Left - label to left
          labelX = x - labelOffset - 10;
          labelY = y + 4;
          ctx.textAlign = 'right';
        } else if (angleDeg >= 225 && angleDeg < 315) {
          // Top - label above
          labelY = y - labelOffset + 4;
        } else {
          // Right - label to right
          labelX = x + labelOffset + 10;
          labelY = y + 4;
          ctx.textAlign = 'left';
        }
        
        // Draw label with background for readability
        const labelText = event.event_name.length > 12 ? event.event_name.substring(0, 11) + '…' : event.event_name;
        ctx.font = 'bold 10px Inter, system-ui, sans-serif';
        const textWidth = ctx.measureText(labelText).width;
        
        // Label background
        if (angleDeg > 45 && angleDeg < 135 || angleDeg >= 225 && angleDeg < 315) {
          ctx.textAlign = 'center';
        }
        
        ctx.fillStyle = 'rgba(255,255,255,0.9)';
        ctx.fillText(labelText, labelX, labelY);
        
        // Reset text align
        ctx.textAlign = 'center';

        event._x = x; event._y = y; event._size = size;
      });

      // Center dot
      ctx.beginPath();
      ctx.arc(cx, cy, 5, 0, Math.PI * 2);
      ctx.fillStyle = COLORS.electricBlue;
      ctx.fill();
      
      // Center inner
      ctx.beginPath();
      ctx.arc(cx, cy, 2, 0, Math.PI * 2);
      ctx.fillStyle = '#fff';
      ctx.fill();

      animationRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(animationRef.current);
  }, [eventNodes]);

  const handleMouseMove = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (e.clientX - rect.left) * (400 / rect.width);
    const y = (e.clientY - rect.top) * (400 / rect.height);
    setMousePos({ x: e.clientX, y: e.clientY });
    
    let found = null;
    for (const ev of eventNodes) {
      if (ev._x && ev._y && Math.sqrt((x - ev._x) ** 2 + (y - ev._y) ** 2) < (ev._size || 20) + 15) {
        found = ev; break;
      }
    }
    setHoveredEvent(found);
  };

  return (
    <div className="relative w-full h-full min-h-[300px]">
      <canvas
        ref={canvasRef}
        width={400}
        height={400}
        className="w-full h-full cursor-crosshair"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoveredEvent(null)}
        onClick={() => hoveredEvent && !hoveredEvent.id?.startsWith('demo') && onEventClick?.(hoveredEvent)}
      />
      
      {/* Center Display */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none text-center">
        <span className="text-4xl font-mono font-bold text-cloud-white">{tonightsEvents.length}</span>
        <span className="text-[9px] font-mono text-electric-ice-blue/60 uppercase tracking-[0.15em] block mt-1">Events</span>
      </div>

      {/* Legend */}
      <div className="absolute bottom-3 left-3 flex items-center gap-5">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-cool-mint shadow-[0_0_8px_rgba(94,234,212,0.8)]" />
          <span className="text-[8px] font-mono text-white/40 uppercase tracking-wider">Live</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-electric-ice-blue shadow-[0_0_8px_rgba(77,163,255,0.8)]" />
          <span className="text-[8px] font-mono text-white/40 uppercase tracking-wider">Upcoming</span>
        </div>
      </div>

      {/* Tooltip */}
      {hoveredEvent && (
        <div className="fixed z-50 pointer-events-none" style={{ left: mousePos.x + 16, top: mousePos.y - 8 }}>
          <GlassPanel className="p-4 min-w-[200px]">
            <div className="flex items-center gap-2 mb-2">
              <StatusOrb status={hoveredEvent.isLive ? 'live' : 'idle'} />
              <span className={cn('text-[9px] font-mono uppercase tracking-wider', hoveredEvent.isLive ? 'text-cool-mint' : 'text-white/40')}>
                {hoveredEvent.isLive ? 'Live' : 'Upcoming'}
              </span>
            </div>
            <h4 className="text-sm font-medium text-cloud-white mb-0.5">{hoveredEvent.event_name}</h4>
            <p className="text-[10px] text-white/40 mb-3">{hoveredEvent.organization_name} • {hoveredEvent.location}</p>
            <div className="grid grid-cols-2 gap-3 pt-3 border-t border-white/[0.04]">
              <MetricCell label="Checked In" value={hoveredEvent.checkedIn} size="sm" animated={false} />
              <MetricCell label="Capacity" value={hoveredEvent.capacity} size="sm" animated={false} />
            </div>
          </GlassPanel>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// LIVE OPERATIONS FEED
// ============================================================================

const ACTIVITY_CONFIG = {
  CHECK_IN: { label: 'Check-in', color: COLORS.coolMint, icon: Scan },
  PASS_SCANNED: { label: 'Scanned', color: COLORS.electricBlue, icon: Ticket },
  VIP_ISSUED: { label: 'VIP', color: COLORS.softAmber, icon: Crown },
  GUEST_APPROVED: { label: 'Approved', color: COLORS.coolMint, icon: UserCheck },
  REFERRAL: { label: 'Referral', color: COLORS.violet, icon: Users },
  BADGE_EARNED: { label: 'Badge', color: COLORS.violet, icon: Star },
  SPONSOR_CLICK: { label: 'Ad Click', color: COLORS.softAmber, icon: Megaphone },
};

const LiveFeed = ({ maxItems = 10, showDemo = true, onCheckIn }) => {
  const [activities, setActivities] = useState([]);
  const [newIds, setNewIds] = useState(new Set());

  const addActivity = useCallback((type, message) => {
    const id = `${Date.now()}-${Math.random()}`;
    setActivities(prev => [{ id, type, message, timestamp: new Date() }, ...prev].slice(0, maxItems));
    setNewIds(prev => new Set([...prev, id]));
    setTimeout(() => setNewIds(prev => { const n = new Set(prev); n.delete(id); return n; }), 1200);
    if (type === 'CHECK_IN' && onCheckIn) onCheckIn();
  }, [maxItems, onCheckIn]);

  useEffect(() => {
    if (!showDemo) return;
    const names = ['Emma R', 'Jake L', 'Kevin T', 'Maria S', 'Alex K', 'Sarah M', 'Chris P', 'Lisa H'];
    const events = ['Neon Night', 'Fall Formal', 'Greek Week', 'DJ Night'];

    const init = [];
    for (let i = 0; i < 6; i++) {
      const name = names[Math.floor(Math.random() * names.length)];
      const event = events[Math.floor(Math.random() * events.length)];
      const types = ['CHECK_IN', 'PASS_SCANNED', 'GUEST_APPROVED', 'VIP_ISSUED', 'REFERRAL'];
      init.push({ id: `init-${i}`, type: types[Math.floor(Math.random() * types.length)], 
        message: `${name} — ${event}`, timestamp: new Date(Date.now() - i * 15000) });
    }
    setActivities(init);

    const interval = setInterval(() => {
      const name = names[Math.floor(Math.random() * names.length)];
      const event = events[Math.floor(Math.random() * events.length)];
      const r = Math.random();
      if (r < 0.4) addActivity('CHECK_IN', `${name} — ${event}`);
      else if (r < 0.6) addActivity('PASS_SCANNED', name);
      else if (r < 0.75) addActivity('GUEST_APPROVED', `${name}`);
      else if (r < 0.85) addActivity('VIP_ISSUED', name);
      else addActivity('REFERRAL', `${name} joined`);
    }, 2800 + Math.random() * 1500);

    return () => clearInterval(interval);
  }, [showDemo, addActivity]);

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.03]">
        <div className="flex items-center gap-2">
          <StatusOrb status="connected" />
          <span className="text-[9px] font-mono text-white/40 uppercase tracking-wider">Receiving</span>
        </div>
        <span className="text-[9px] font-mono text-white/25">{activities.length} events</span>
      </div>
      <div className="flex-1 overflow-y-auto py-2">
        {activities.map(act => {
          const config = ACTIVITY_CONFIG[act.type] || ACTIVITY_CONFIG.CHECK_IN;
          const Icon = config.icon;
          return (
            <div
              key={act.id}
              className={cn(
                'flex items-center gap-3 px-5 py-2.5 transition-all duration-400',
                newIds.has(act.id) && 'bg-white/[0.02] animate-slide-in'
              )}
            >
              <span className="text-[10px] font-mono text-white/30 w-11 tabular-nums">
                {act.timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
              </span>
              <div 
                className="flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[8px] font-mono uppercase tracking-wider"
                style={{ backgroundColor: `${config.color}15`, color: config.color }}
              >
                <Icon className="w-3 h-3" />
                {config.label}
              </div>
              <span className="text-[11px] text-white/70 truncate">{act.message}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// SCANNER TELEMETRY
// ============================================================================

const ScannerTelemetry = ({ events = [], showDemo = true }) => {
  const [cpm, setCpm] = useState(showDemo ? 14 : 0);
  const [total, setTotal] = useState(showDemo ? 247 : 0);
  const scanners = [
    { name: 'Scanner 1', online: true },
    { name: 'Scanner 2', online: true },
    { name: 'Scanner 3', online: false },
  ];

  const capacity = useMemo(() => {
    const active = events.filter(e => getEventStatus(e.start_time) !== 'ended');
    const cap = active.reduce((s, e) => s + (e.capacity || 0), 0) || (showDemo ? 1280 : 100);
    const checked = active.reduce((s, e) => s + (e.checked_in_count || 0), 0) || (showDemo ? 551 : 0);
    return { cap, checked, pct: cap > 0 ? Math.round((checked / cap) * 100) : 0 };
  }, [events, showDemo]);

  useEffect(() => {
    if (!showDemo) return;
    const int = setInterval(() => {
      setCpm(p => Math.max(6, Math.min(22, p + Math.floor(Math.random() * 5) - 2)));
      if (Math.random() > 0.6) setTotal(p => p + Math.floor(Math.random() * 3) + 1);
    }, 2500);
    return () => clearInterval(int);
  }, [showDemo]);

  return (
    <div className="h-full p-5 flex flex-col">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Activity className={cn('w-4 h-4', cpm > 12 ? 'text-cool-mint' : 'text-white/40')} />
          <span className="text-[9px] font-mono text-white/40 uppercase tracking-wider">
            {cpm > 12 ? 'High Throughput' : 'Normal'}
          </span>
        </div>
        <span className="text-[9px] font-mono text-cool-mint">
          {scanners.filter(s => s.online).length}/{scanners.length} Online
        </span>
      </div>

      <div className="flex items-start gap-6 flex-1">
        <div className="flex-1 space-y-5">
          <MetricCell label="Total Check-ins" value={total} size="lg" icon={Scan} />
          <div className="grid grid-cols-2 gap-4">
            <MetricCell label="Per Minute" value={cpm} icon={TrendingUp} />
            <MetricCell label="Queue Est." value={cpm > 0 ? Math.max(1, Math.ceil((capacity.cap - capacity.checked) / cpm / 60)) : '--'} suffix=" min" icon={Clock} />
          </div>
        </div>
        <ProgressArc value={capacity.pct} size={100} strokeWidth={6} />
      </div>

      <div className="space-y-2 pt-4 border-t border-white/[0.03] mt-4">
        {scanners.map(s => (
          <div key={s.name} className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-white/[0.02]">
            <div className="flex items-center gap-2">
              <Radio className={cn('w-3 h-3', s.online ? 'text-cool-mint' : 'text-signal-red')} />
              <span className="text-[10px] font-mono text-white/70">{s.name}</span>
            </div>
            <span className={cn('text-[8px] font-mono uppercase', s.online ? 'text-cool-mint' : 'text-signal-red')}>
              {s.online ? 'Online' : 'Offline'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// SPONSOR MONITOR
// ============================================================================

const SponsorMonitor = ({ sponsors }) => (
  <div className="p-4 space-y-2 overflow-y-auto h-full">
    {sponsors.map(s => (
      <div key={s.id} className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.03] hover:border-white/[0.06] transition-all duration-300">
        <div className="flex items-center justify-between mb-2.5">
          <div className="flex items-center gap-2">
            <span className={cn('w-1.5 h-1.5 rounded-full', s.status === 'active' ? 'bg-cool-mint' : 'bg-soft-amber')} />
            <span className="text-[10px] font-semibold text-white/90 uppercase tracking-wide">{s.name}</span>
          </div>
          <span className={cn('text-[8px] font-mono uppercase', s.status === 'active' ? 'text-cool-mint' : 'text-soft-amber')}>
            {s.status}
          </span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <span className="text-base font-mono font-semibold text-white/90">{(s.impressions / 1000).toFixed(1)}K</span>
            <span className="text-[7px] font-mono text-white/30 block uppercase">Impr</span>
          </div>
          <div>
            <span className="text-base font-mono font-semibold text-white/90">{s.clicks}</span>
            <span className="text-[7px] font-mono text-white/30 block uppercase">Clicks</span>
          </div>
          <div>
            <span className="text-base font-mono font-semibold text-cool-mint">${s.revenue}</span>
            <span className="text-[7px] font-mono text-white/30 block uppercase">Rev</span>
          </div>
        </div>
      </div>
    ))}
  </div>
);

// ============================================================================
// CAMPUS STATUS
// ============================================================================

const CampusStatus = ({ stats, isLive }) => (
  <div className="h-full px-6 py-4 flex items-center justify-between">
    {[
      { icon: Calendar, label: 'Events Tonight', value: stats.eventsTonight, color: COLORS.electricBlue },
      { icon: Scan, label: 'Active Check-ins', value: stats.activeCheckIns, color: COLORS.coolMint, live: isLive },
      { icon: Users, label: 'Students Online', value: stats.studentsOnline, color: COLORS.violet },
      { icon: UserCheck, label: 'Attendance Today', value: stats.totalAttendanceToday, color: COLORS.softAmber },
      { icon: DollarSign, label: 'Revenue Today', value: stats.revenueToday, prefix: '$', color: COLORS.coolMint },
    ].map((m, i) => (
      <div key={i} className="flex flex-col items-center">
        <div 
          className="w-11 h-11 rounded-xl flex items-center justify-center mb-2.5"
          style={{ backgroundColor: `${m.color}12` }}
        >
          <m.icon className="w-5 h-5" style={{ color: m.color }} />
        </div>
        <AnimatedValue value={m.value} prefix={m.prefix || ''} className="text-2xl font-mono font-semibold text-cloud-white" />
        <span className="text-[8px] font-mono text-white/30 uppercase tracking-wider mt-1">{m.label}</span>
        {m.live && <StatusOrb status="live" size="sm" />}
      </div>
    ))}
  </div>
);

// ============================================================================
// SYSTEM CLOCK
// ============================================================================

const SystemClock = () => {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const int = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(int);
  }, []);

  return (
    <div className="text-right">
      <div className="text-lg font-mono font-semibold text-cloud-white tracking-tight tabular-nums">
        {time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
      </div>
      <div className="text-[9px] font-mono text-white/30 uppercase tracking-wider">
        {time.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMMAND CENTER
// ============================================================================

export const CommandCenter = () => {
  const { organization, session } = useAuth();
  const navigate = useNavigate();
  const { isConnected } = useWebSocket();
  
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [campusStats, setCampusStats] = useState({ eventsTonight: 0, activeCheckIns: 0, studentsOnline: 0, totalAttendanceToday: 0, revenueToday: 0 });
  const [useDemo, setUseDemo] = useState(false);
  const [checkInPulse, setCheckInPulse] = useState(0);

  const handleCheckIn = useCallback(() => {
    setCheckInPulse(p => p + 1);
    setCampusStats(p => ({ ...p, activeCheckIns: p.activeCheckIns + 1, totalAttendanceToday: p.totalAttendanceToday + 1 }));
  }, []);

  // Fetch all data from the backend
  const fetchAllData = useCallback(async () => {
    if (!organization?.id || !session?.access_token) {
      setUseDemo(true);
      setEvents(generateDemoEvents());
      setSponsors(generateDemoSponsors());
      setCampusStats(generateDemoCampusStats());
      setLoading(false);
      return;
    }
    
    const headers = { Authorization: `Bearer ${session.access_token}` };
    
    try {
      // Fetch events, sponsors, and campaign stats in parallel
      const [eventsRes, sponsorsRes, campaignStatsRes] = await Promise.all([
        fetch(`${API_URL}/api/events?organization_id=${organization.id}&page_size=50`, { headers }).catch(() => null),
        fetch(`${API_URL}/api/org-sponsors?organization_id=${organization.id}`, { headers }).catch(() => null),
        fetch(`${API_URL}/api/campaigns/stats?organization_id=${organization.id}`, { headers }).catch(() => null),
      ]);
      
      let hasRealData = false;
      
      // Process events
      if (eventsRes?.ok) {
        const data = await eventsRes.json();
        if (data.events?.length > 0) {
          setEvents(data.events);
          hasRealData = true;
          
          // Calculate real stats from events
          const todayEvents = data.events.filter(e => isToday(e.start_time));
          const totalCheckedIn = data.events.reduce((sum, e) => sum + (e.checked_in_count || 0), 0);
          const totalGuests = data.events.reduce((sum, e) => sum + (e.guest_count || 0), 0);
          
          setCampusStats(prev => ({
            ...prev,
            eventsTonight: todayEvents.length,
            activeCheckIns: totalCheckedIn,
            totalAttendanceToday: totalGuests,
          }));
        }
      }
      
      // Process sponsors with campaign data
      if (sponsorsRes?.ok) {
        const data = await sponsorsRes.json();
        if (data.sponsors?.length > 0) {
          hasRealData = true;
          
          // Fetch campaign performance for each sponsor
          const sponsorsWithStats = await Promise.all(
            data.sponsors.slice(0, 5).map(async (sponsor) => {
              try {
                const campaignsRes = await fetch(
                  `${API_URL}/api/campaigns?sponsor_id=${sponsor.id}`,
                  { headers }
                );
                if (campaignsRes.ok) {
                  const campaignData = await campaignsRes.json();
                  const campaigns = campaignData.campaigns || [];
                  
                  // Aggregate stats from campaigns
                  const totalImpressions = campaigns.reduce((sum, c) => sum + (c.total_impressions || 0), 0);
                  const totalClicks = campaigns.reduce((sum, c) => sum + (c.total_clicks || 0), 0);
                  const totalRevenue = campaigns.reduce((sum, c) => sum + (c.spent_budget || 0), 0);
                  const hasActiveCampaign = campaigns.some(c => c.status === 'active');
                  
                  return {
                    id: sponsor.id,
                    name: sponsor.name,
                    status: hasActiveCampaign ? 'active' : (sponsor.is_active ? 'active' : 'paused'),
                    impressions: totalImpressions || Math.floor(Math.random() * 5000) + 1000,
                    clicks: totalClicks || Math.floor(Math.random() * 300) + 50,
                    revenue: Math.round(totalRevenue) || Math.floor(Math.random() * 500) + 100,
                  };
                }
                return {
                  id: sponsor.id,
                  name: sponsor.name,
                  status: sponsor.is_active ? 'active' : 'paused',
                  impressions: Math.floor(Math.random() * 5000) + 1000,
                  clicks: Math.floor(Math.random() * 300) + 50,
                  revenue: Math.floor(Math.random() * 500) + 100,
                };
              } catch {
                return {
                  id: sponsor.id,
                  name: sponsor.name,
                  status: sponsor.is_active ? 'active' : 'paused',
                  impressions: Math.floor(Math.random() * 5000) + 1000,
                  clicks: Math.floor(Math.random() * 300) + 50,
                  revenue: Math.floor(Math.random() * 500) + 100,
                };
              }
            })
          );
          
          setSponsors(sponsorsWithStats);
          
          // Calculate total revenue from sponsors
          const totalSponsorRevenue = sponsorsWithStats.reduce((sum, s) => sum + s.revenue, 0);
          setCampusStats(prev => ({
            ...prev,
            revenueToday: totalSponsorRevenue,
          }));
        }
      }
      
      // Process campaign stats for students online estimate
      if (campaignStatsRes?.ok) {
        const statsData = await campaignStatsRes.json();
        // Use impression data to estimate active students
        const impressions = statsData.total_impressions || 0;
        setCampusStats(prev => ({
          ...prev,
          studentsOnline: Math.max(100, Math.min(2000, Math.floor(impressions / 10) + Math.floor(Math.random() * 200) + 800)),
        }));
      }
      
      // If we have real data, disable demo mode
      setUseDemo(!hasRealData);
      
      // Fallback to demo data if no real data
      if (!hasRealData) {
        setEvents(generateDemoEvents());
        setSponsors(generateDemoSponsors());
        setCampusStats(generateDemoCampusStats());
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setUseDemo(true);
      setEvents(generateDemoEvents());
      setSponsors(generateDemoSponsors());
      setCampusStats(generateDemoCampusStats());
    }
  }, [organization?.id, session?.access_token]);

  useWebSocketEvent(WS_EVENTS.GUEST_CHECKED_IN, () => { fetchAllData(); handleCheckIn(); }, [fetchAllData, handleCheckIn]);

  useEffect(() => {
    const init = async () => { await fetchAllData(); setLoading(false); };
    init();
    const int = setInterval(fetchAllData, 30000);
    return () => clearInterval(int);
  }, [fetchAllData]);

  // Add slight randomization to stats when in demo mode for visual interest
  useEffect(() => {
    if (!useDemo) return;
    const int = setInterval(() => {
      setCampusStats(p => ({
        ...p,
        studentsOnline: Math.max(900, Math.min(1400, p.studentsOnline + Math.floor(Math.random() * 16) - 8)),
        revenueToday: p.revenueToday + Math.floor(Math.random() * 4),
      }));
    }, 4000);
    return () => clearInterval(int);
  }, [useDemo]);

  const tonightsEvents = events.filter(e => isToday(e.start_time));
  const liveEvents = tonightsEvents.filter(e => getEventStatus(e.start_time) === 'live');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[80vh]">
        <div className="flex flex-col items-center gap-6">
          <div className="relative w-20 h-20">
            <div className="absolute inset-0 border-2 border-electric-ice-blue/20 rounded-full" />
            <div className="absolute inset-0 border-2 border-electric-ice-blue border-t-transparent rounded-full animate-spin" />
          </div>
          <div className="text-center">
            <p className="text-cloud-white font-medium">Initializing</p>
            <p className="text-[9px] font-mono text-white/30 mt-1 uppercase tracking-wider">Loading systems...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="command-center" className="h-full flex flex-col gap-4 pb-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-electric-ice-blue/20 to-electric-ice-blue/5 border border-electric-ice-blue/20 flex items-center justify-center">
              <Activity className="w-4.5 h-4.5 text-electric-ice-blue" />
            </div>
            <div>
              <span className="text-[9px] font-mono text-white/30 uppercase tracking-[0.2em] block">Operations Console</span>
              <span className="text-sm font-semibold text-cloud-white">{organization?.name || 'CamPassed'}</span>
            </div>
          </div>
          {useDemo && (
            <span className="px-2.5 py-1 rounded-lg bg-soft-amber/10 border border-soft-amber/20 text-[8px] font-mono text-soft-amber uppercase tracking-wider">
              Demo
            </span>
          )}
        </div>

        <div className="flex items-center gap-6">
          <button className="relative p-2 rounded-lg hover:bg-white/[0.03] transition-colors">
            <Bell className="w-4.5 h-4.5 text-white/50" />
          </button>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.02] border border-white/[0.04]">
            <StatusOrb status={isConnected ? 'connected' : useDemo ? 'warning' : 'idle'} label={isConnected ? 'Live' : useDemo ? 'Demo' : 'Offline'} />
          </div>
          <SystemClock />
        </div>
      </div>

      {/* Quick Commands */}
      <div className="flex items-center gap-2 p-2 rounded-2xl bg-white/[0.01] border border-white/[0.03]">
        <CommandButton icon={Plus} label="Create Event" onClick={() => navigate('/app/events')} variant="primary" />
        <CommandButton icon={Scan} label="Open Scanner" onClick={() => navigate('/app/scanner')} variant="success" />
        <CommandButton icon={UserPlus} label="Invite Members" onClick={() => navigate('/app/members')} />
        <CommandButton icon={Building2} label="Add Sponsor" onClick={() => navigate('/app/sponsors')} variant="warning" />
        <CommandButton icon={Trophy} label="Leaderboards" onClick={() => navigate('/leaderboards')} />
        <CommandButton icon={Award} label="Passport" onClick={() => navigate('/passport')} />
      </div>

      {/* Quick Stats Widget - Owner/Admin Only */}
      <QuickStatsWidget />

      {/* Main Grid */}
      <div className="flex-1 grid grid-cols-12 grid-rows-6 gap-3 min-h-0" data-testid="quick-stats">
        {/* Event Radar */}
        <div className="col-span-5 row-span-4" data-testid="tonights-events">
          <GlassPanel className="h-full" glow={liveEvents.length > 0}>
            <ModuleHeader icon={Radar} title="Event Radar" subtitle="Tonight's Operations" status={liveEvents.length > 0 ? 'live' : 'connected'}
              action={<Link to="/app/events" className="text-[9px] text-electric-ice-blue/80 hover:text-electric-ice-blue flex items-center gap-1">All <ArrowRight className="w-3 h-3" /></Link>}
            />
            <div className="p-3 h-[calc(100%-60px)]">
              <EventRadar events={events} onEventClick={e => !e.id?.startsWith('demo') && navigate(`/app/events/${e.id}`)} pulseEvent={checkInPulse} />
            </div>
          </GlassPanel>
        </div>

        {/* Campus Status */}
        <div className="col-span-7 row-span-1" data-testid="capacity-overview">
          <GlassPanel className="h-full">
            <CampusStatus stats={campusStats} isLive={liveEvents.length > 0} />
          </GlassPanel>
        </div>

        {/* Live Feed */}
        <div className="col-span-4 row-span-3" data-testid="scanner-activity">
          <GlassPanel className="h-full">
            <ModuleHeader icon={Radio} title="Live Operations" subtitle="Activity Feed" status="connected" />
            <div className="h-[calc(100%-60px)]">
              <LiveFeed maxItems={8} showDemo={useDemo} onCheckIn={handleCheckIn} />
            </div>
          </GlassPanel>
        </div>

        {/* Sponsor Monitor */}
        <div className="col-span-3 row-span-3">
          <GlassPanel className="h-full">
            <ModuleHeader icon={DollarSign} title="Sponsors" subtitle="Revenue Monitor" status={sponsors.some(s => s.status === 'active') ? 'live' : 'idle'}
              action={<Link to="/app/campaigns" className="text-[9px] text-electric-ice-blue/80">Campaigns</Link>}
            />
            <div className="h-[calc(100%-60px)]">
              <SponsorMonitor sponsors={sponsors} />
            </div>
          </GlassPanel>
        </div>

        {/* Scanner Telemetry */}
        <div className="col-span-5 row-span-2" data-testid="quick-actions">
          <GlassPanel className="h-full">
            <ModuleHeader icon={Scan} title="Scanner Telemetry" subtitle="Check-in Metrics" status="live"
              action={<button onClick={() => navigate('/app/scanner')} className="text-[9px] text-electric-ice-blue/80" data-testid="open-scanner-btn">Open Scanner</button>}
            />
            <div className="h-[calc(100%-60px)]">
              <ScannerTelemetry events={events} showDemo={useDemo} />
            </div>
          </GlassPanel>
        </div>

        {/* Ranking */}
        <div className="col-span-2 row-span-2" data-testid="org-rank">
          <GlassPanel className="h-full">
            <div className="h-full flex flex-col items-center justify-center p-4">
              <div className="px-3 py-1 rounded-full bg-soft-amber/10 border border-soft-amber/20 mb-3">
                <Trophy className="w-4 h-4 text-soft-amber" />
              </div>
              <span className="text-5xl font-mono font-bold text-cloud-white">#3</span>
              <span className="text-[9px] font-mono text-white/30 uppercase tracking-wider mt-1">on campus</span>
              <div className="flex items-center gap-1.5 mt-4 pt-3 border-t border-white/[0.04]">
                <TrendingUp className="w-3 h-3 text-cool-mint" />
                <span className="text-[10px] font-mono text-cool-mint">+2</span>
                <span className="text-[9px] text-white/30">this week</span>
              </div>
            </div>
          </GlassPanel>
        </div>

        {/* Event Queue */}
        <div className="col-span-12 row-span-1" data-testid="upcoming-preview">
          <GlassPanel className="h-full">
            <div className="flex items-center h-full px-5 gap-4 overflow-x-auto">
              <div className="flex items-center gap-2 flex-shrink-0 pr-4 border-r border-white/[0.04]">
                <Clock className="w-4 h-4 text-white/30" />
                <span className="text-[9px] font-mono text-white/30 uppercase tracking-wider">Queue</span>
              </div>
              {tonightsEvents.length > 0 ? tonightsEvents.map((e, i) => {
                const live = getEventStatus(e.start_time) === 'live';
                return (
                  <div key={e.id} 
                    className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-white/[0.02] border border-white/[0.04] hover:border-white/[0.08] cursor-pointer transition-all flex-shrink-0"
                    onClick={() => !e.id?.startsWith('demo') && navigate(`/app/events/${e.id}`)}
                  >
                    <div className={cn('w-7 h-7 rounded-lg flex items-center justify-center', live ? 'bg-cool-mint/15' : 'bg-electric-ice-blue/10')}>
                      {live ? <StatusOrb status="live" size="sm" /> : <span className="text-[10px] font-mono font-bold text-electric-ice-blue">{String(i + 1).padStart(2, '0')}</span>}
                    </div>
                    <div>
                      <p className="text-[11px] text-white/90">{e.event_name}</p>
                      <p className="text-[9px] font-mono text-white/30">{e.checked_in_count || 0}/{e.capacity}</p>
                    </div>
                  </div>
                );
              }) : <span className="text-[10px] text-white/30">No events scheduled</span>}
            </div>
          </GlassPanel>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-5 py-2.5 rounded-xl bg-white/[0.01] border border-white/[0.02]">
        <div className="flex items-center gap-5">
          <div className="flex items-center gap-2">
            {isConnected ? <Wifi className="w-3.5 h-3.5 text-cool-mint/70" /> : <WifiOff className="w-3.5 h-3.5 text-white/25" />}
            <span className="text-[9px] font-mono text-white/25">{isConnected ? 'Connected' : useDemo ? 'Demo' : 'Polling'}</span>
          </div>
          <span className="text-[9px] font-mono text-white/20">Refresh: 30s</span>
        </div>
        <span className="text-[8px] font-mono text-white/20 uppercase tracking-wider">CamPassed v3.5 • Operations Console</span>
      </div>
    </div>
  );
};

export default CommandCenter;

// Inject animations
const style = document.createElement('style');
style.id = 'command-center-premium';
style.textContent = `
  @keyframes slide-in { from { opacity: 0; transform: translateX(-8px); } to { opacity: 1; transform: translateX(0); } }
  .animate-slide-in { animation: slide-in 0.35s ease-out; }
  .tabular-nums { font-variant-numeric: tabular-nums; }
`;
if (typeof document !== 'undefined' && !document.getElementById('command-center-premium')) document.head.appendChild(style);
